import { FormController, Icon, Tooltip, UseFormReturn, appendClassNameByCondition } from '@gcl/react-core';
import { RowState } from '@gcl/react-core/dist/components/Table/UnstyledTableComponents/Layout/VerticalTableLayout';
import { v4 as uuidV4 } from 'uuid';
import * as React from 'react';
import { IUploadProjectFileAsyncProps } from '@file/projectFile/projectFile.services';
import {
  IFormFileDrawingItem,
  IProjectFileDrawingTempChunkCompleteRequest,
} from '@file/projectFile/projectFile.models';
import { useProjectId } from '@config/config.hooks';
import { IProjectTransmittalItemDocumentType } from '@transmittal/transmittalItem/transmittalItemDocumentType/transmittalItemDocumentType.models';
import { FileUploadLoader } from '@file/FileUploadLoader';
import { EProjectFileType } from '@file/projectFile/projectFile.constants';
import { useFormPrefix, useUploadProjectFileDrawingTemp } from './transmittalItemTableForm.hooks';
import { ETransmittalItemTableFormKey } from './transmittalItemTableForm.constants';

export interface ITransmittalItemFileUploadProps {
  setRowState: React.Dispatch<React.SetStateAction<RowState>> | undefined;
  setIsBlockRefetch: (value: React.SetStateAction<boolean>) => void;
  cellExtras: any;
  docType: IProjectTransmittalItemDocumentType;
  item: IFormFileDrawingItem;
  tableIndex: number;
  rowIndex: number;
  useFormMethods: UseFormReturn;
  newTransmittalItemFile?: File | null | undefined;
  newTransmittalItemFileUuid?: string;
  defaultFileType: EProjectFileType | undefined;
  setFilesToUpload: React.Dispatch<
    React.SetStateAction<{
      [key: string]: File;
    }>
  >;
}

export const TransmittalItemFileUpload: React.FC<ITransmittalItemFileUploadProps> = ({
  setRowState,
  setIsBlockRefetch,
  cellExtras,
  docType,
  item,
  rowIndex,
  useFormMethods,
  tableIndex,
  newTransmittalItemFile,
  newTransmittalItemFileUuid,
  defaultFileType,
  setFilesToUpload,
}) => {
  const { tablePrefix, rowPrefix, rowFormPrefix } = useFormPrefix(tableIndex, rowIndex);

  const projectId = useProjectId();

  const [uuid, setUuid] = React.useState<string>();

  const {
    mutationUploadProjectFileDrawingTemp,
    renderErrorConfirmationPortal,
    openErrorConfirmationPortal,
    uploadProgress,
    setUploadProgress,
  } = useUploadProjectFileDrawingTemp(
    item,
    uuid,
    tableIndex,
    rowIndex,
    useFormMethods,
    setFilesToUpload,
    setIsBlockRefetch,
    setRowState,
  );
  const {
    mutate: mutateUploadProjectFileDrawingTemp,
    data: dataUploadProjectFileDrawingTemp,
    error: errorUploadProjectFileDrawingTemp,
    reset: resetUploadProjectFileDrawingTemp,
    isLoading: isLoadingUploadProjectFileDrawingTemp,
    isError: isErrorUploadProjectFileDrawingTemp,
    isSuccess: isSuccessUploadProjectFileDrawingTemp,
  } = mutationUploadProjectFileDrawingTemp;

  const [file, setFile] = React.useState<File>();

  const [projectFile, setProjectFile] = React.useState<IProjectFileDrawingTempChunkCompleteRequest>();

  React.useEffect(() => {
    if (newTransmittalItemFile) {
      setFile(newTransmittalItemFile);
      setProjectFile(undefined);
    }
  }, [newTransmittalItemFile]);
  React.useEffect(() => {
    if (uuid !== newTransmittalItemFileUuid) setUuid(newTransmittalItemFileUuid);
  }, [newTransmittalItemFileUuid]);

  React.useEffect(() => {
    if (!file || !uuid || !!projectFile) return;
    const projectFileUploadState = useFormMethods.getValues(
      `${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`,
    );
    if (projectFileUploadState !== 'uploadItem' && projectFileUploadState !== 'uploadRevision') return;

    setIsBlockRefetch(true);
    const uploadRequest: IUploadProjectFileAsyncProps<IProjectFileDrawingTempChunkCompleteRequest> = {
      request: {
        fileType: defaultFileType,
        projectId,
        fileName: file?.name,
        file,
        uuid: uuid ?? uuidV4(),
        isTemp: true,
      },
      onUploadProgress: (progress) => {
        setUploadProgress(progress);
      },
    };

    setUploadProgress(0);
    if (projectFileUploadState === 'uploadItem')
      useFormMethods.setValue(
        `${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`,
        'uploadingItem',
      );
    if (projectFileUploadState === 'uploadRevision')
      useFormMethods.setValue(
        `${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFileUploadState}`,
        'uploadingRevision',
      );
    setProjectFile(uploadRequest.request);
    mutateUploadProjectFileDrawingTemp(uploadRequest);
  }, [uuid]);

  return (
    <>
      <FileUploadLoader
        uploadProgress={uploadProgress}
        error={errorUploadProjectFileDrawingTemp}
        fileName={projectFile?.fileName}
        render={() => {
          return (
            <>
              <Tooltip overlay="Upload New Revision" id="upload-new-revision" key="upload-new-file">
                <label
                  className="gcl-gems-transmittal-item-upload-btn"
                  onChange={(e: any) => {
                    const inputElement = e.target as HTMLInputElement;
                    const { files } = inputElement;
                    const selectedFile = files?.item(0);
                    if (!selectedFile) {
                      setIsBlockRefetch(false);
                      return;
                    }

                    const newUuid = uuidV4();
                    setProjectFile(undefined);
                    setFile(selectedFile);
                    setUuid(newUuid);

                    setFilesToUpload((old) => {
                      return { ...old, [newUuid]: selectedFile };
                    });
                    useFormMethods.setValue(`${rowFormPrefix}.${ETransmittalItemTableFormKey.uuid}`, newUuid);

                    useFormMethods.setValue(`${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFile}`, {
                      uuid: newUuid,
                      fileName: selectedFile.name,
                      uploadState: 'uploadRevision',
                      fileType: defaultFileType,
                      projectId,
                    });
                  }}
                  htmlFor={`gcl-gems-transmittal-document-table-${docType.id}-file-input-${
                    item?.fileInfo?.id || rowIndex
                  }`}
                >
                  {!cellExtras?.isAnyActionLoading && (
                    <input
                      name=""
                      type="file"
                      accept="application/pdf"
                      id={`gcl-gems-transmittal-document-table-${docType.id}-file-input-${
                        item?.fileInfo?.id || rowIndex
                      }`}
                      hidden
                      onClick={(e) => {
                        const element = e.target as HTMLInputElement;
                        element.value = '';
                      }}
                    />
                  )}
                  <Icon
                    iconName="Upload"
                    className={appendClassNameByCondition(
                      cellExtras?.isAnyActionLoading,
                      'gcl-files-display-icon-button gcl-ic-btn-lg gcl-fc-primary-400',
                      'gcl-cursor-default',
                    )}
                    onClick={() => setIsBlockRefetch(true)}
                  />
                </label>
              </Tooltip>
              {errorUploadProjectFileDrawingTemp && item?.form?.projectFile?.uploadState === 'error' && (
                <Icon
                  iconName="ExclamationTriangle"
                  className="gcl-gems-transmittal-item-upload-revision-fail-icon gcl-ic-btn-lg"
                  onClick={openErrorConfirmationPortal}
                />
              )}
            </>
          );
        }}
      />
      <FormController
        formFieldMethods={useFormMethods}
        renderInputControl={({ field, fieldState, formState, id, setValue }) => {
          return <></>;
        }}
        id={`${rowFormPrefix}.${ETransmittalItemTableFormKey.projectFile}`}
      />
      {renderErrorConfirmationPortal(false, false, false)}
    </>
  );
};
